import { useEffect, useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { BrandMark } from "@/components/brand/BrandMark";
import { useAuth } from "@/lib/auth/AuthContext";
import { ApiError } from "@/lib/api";
import { Button, FormField, Input } from "@/components/ui";
import { AuthLayout } from "./AuthLayout";

const GENERIC_LOGIN_ERROR =
  "No hemos podido iniciar sesion con esos datos. Comprueba el correo y la contrasena.";

export function LoginPage() {
  const { state, submitLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const pendingRoute =
    state.status === "totp_challenge"
      ? state.requiresEnrollment
        ? "/login/enroll"
        : "/login/totp"
      : null;

  useEffect(() => {
    if (pendingRoute) {
      navigate(pendingRoute, { replace: true });
    }
  }, [pendingRoute, navigate]);

  if (state.status === "authenticated") {
    return <Navigate to="/" replace />;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await submitLogin(email.trim(), password);
    } catch (err) {
      // Security invariant: never reveal whether the account exists or
      // which factor was wrong. Always show the same generic message.
      if (err instanceof ApiError && err.status === 429) {
        setError("Demasiados intentos. Espera unos minutos antes de volver a intentarlo.");
      } else {
        setError(GENERIC_LOGIN_ERROR);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout
      icon={<BrandMark className="h-8 w-8" />}
      title="Acceso administrativo"
      description="Panel privado de Shalom App Control. Solo administradores autorizados."
    >
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <FormField label="Correo electronico" required>
          {(field) => (
            <Input
              {...field}
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
            />
          )}
        </FormField>

        <FormField label="Contrasena" required>
          {(field) => (
            <Input
              {...field}
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
            />
          )}
        </FormField>

        {error && (
          <p role="alert" className="rounded-lg bg-danger-50 px-3 py-2 text-sm text-danger-700">
            {error}
          </p>
        )}

        <Button type="submit" className="w-full" loading={submitting}>
          Continuar
        </Button>
      </form>
    </AuthLayout>
  );
}
